import { useQuery } from "@tanstack/react-query";
import { CloudCheck, CloudOff } from "lucide-react";
import { api, apiBaseUrl } from "@/api/client";
import { useAuth } from "@/auth/AuthContext";
import { cn } from "@/lib/utils";

/**
 * The live API status line at the foot of Today.
 *
 * This is what is left of the old debug HomeScreen: rather than dumping
 * `/health` and `/auth/me` as JSON, it verifies both quietly and only says
 * something when one of them is unhappy. Keeping it on the default screen means
 * a broken deploy is visible without opening devtools.
 */
export function ConnectionStatus() {
  const { accessToken } = useAuth();

  const health = useQuery({
    queryKey: ["health"],
    queryFn: async () => {
      const res = await api.health.health();
      if (res.status !== 200) throw new Error("API unhealthy");
      return res.body;
    },
  });

  const me = useQuery({
    queryKey: ["me"],
    enabled: Boolean(accessToken),
    queryFn: async () => {
      const res = await api.auth.me({
        headers: { authorization: `Bearer ${accessToken ?? ""}` },
      });
      if (res.status !== 200) throw new Error("Session expired");
      return res.body.user;
    },
  });

  const offline = health.isError;
  const sessionStale = me.isError;
  const ok = !offline && !sessionStale;

  return (
    <div className="mt-6 flex items-center gap-2.5 text-[0.78rem] font-semibold text-faint">
      <span className={cn("flex", ok ? "text-ok" : "text-danger")}>
        {ok ? <CloudCheck size={15} /> : <CloudOff size={15} />}
      </span>
      <span>
        {offline
          ? "API unreachable"
          : sessionStale
            ? "Session expired — sign in again"
            : "All synced"}
      </span>
      <span className="text-border-hover">·</span>
      <span className="font-mono font-normal">{apiBaseUrl}</span>
    </div>
  );
}
